import React from "react";
import { Currency, Payment, PaymentSetting, PaymentSettingData, PaymentStatus, PaymentType, Settings } from "../../http/protocol";
import { Form } from "../form/Form";
import {FormGroup} from "../form/FormGroup";
import { LoadingViewState, RouterView } from "../RouterView";
import {RouteComponentProps, withRouter} from "react-router";
import { getContainer } from "../../ioc/IOCSetup";
import { ErrorModal } from "../../util/ErrorModal";
import { Submit } from "../form/Submit";
import { FormInput, FormMap, StaticFormValue } from "../form/FormInput";
import { Validators } from "../form/Validators";
import './PaymentEdit.scss';
import { ConfirmModal } from "../../util/ConfirmModal";
import { PaymentSettingService } from "../../services/PaymentSettingService";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";

interface State extends LoadingViewState {
    paymentSetting? : PaymentSetting;
    paymentSettingValue? : { [index: string]: String }; 
    validationMap? : any;
    error?: boolean;
    errorMessage?: string;
    confirmModal?:boolean;
    confirmMessage?: string;
    expanded?: boolean;
}

interface Props extends RouteComponentProps{ 

}

export class PaymentSettingEdit extends RouterView<Props, State>{
    
    service : PaymentSettingService; 

    constructor(props : Props){
        super(props);
        this.service = getContainer().getPaymentSettingService();
        this.state = { validationMap : {}, error: false, expanded:false};
    }

    getTitle(): string {
        return "Edit Payment Setting";
    }

    getSubTitle(): string {
        return this.state.paymentSetting ? this.state.paymentSetting.comments : "";
    }

    componentDidUpdate(prevProps : RouteComponentProps, prevState : State) {
        if((prevProps.match.params as any).id !== (this.props.match.params as any).id)  {
            this.refreshContent();
        }
    }

    /**
     * Because some of the setting keys can have dots in them, we need to format them
     * so that the form can handle them
     * @param key unformatted key
     * @returns 
     */
    formatKey(key : string) {
        return key.replaceAll(".", "__").trim();
    }

    /**
     * Because we had to format the keys, we need to unformat them before we save them
     * or reference them in the paymentSetting object
     * @param key formatted key
     * @returns 
     */
    unformatKey(key : string) {
        return key.replaceAll("__", ".").trim();
    }

    /**
     * Load the payment setting and format the keys
     * Must use paymentSettingValue as placeholder for the form because of the dots in the keys
     * @returns
     */
    loadContent(): void | Promise<any> {
        let id = (this.props.match.params as any).id;
        return this.service.getEntity(id).then( (r : PaymentSetting) => {
            let value : { [index: string]: String } = {};
            for (let key of Object.keys(r!.value!)) {
                if(!key) continue;
                let formattedKey = this.formatKey(key);
                value[formattedKey] = r!.value![key].value;
            };
            this.setState({paymentSetting:r, paymentSettingValue:value});
        }) 
    }

    onSubmit()  {        
        if(!this.state.paymentSetting) return;  

        this.service.save(this.state.paymentSetting!).then((a : Payment)=> {
            this.loadContent();
        });
    }

    /**
     * Because the form must use paymentSettingValue as placeholder because of the dots in the original keys
     * we need to update the paymentSettingValue object with the new values from the form
     * and update the paymentSetting object with the new values from the form unformatted keys
     * @param formModel 
     * @param validationMap 
     */
    formDidUpdate(formModel: Payment, validationMap?: any) {
        
        let paymentSetting = Object.assign({}, this.state.paymentSetting);
        for (let key of Object.keys(formModel!)) {
            if(this.state.paymentSetting!.value![this.unformatKey(key)]) {
                paymentSetting!.value![this.unformatKey(key)].value = formModel![key];
            }
        }

        this.setState({
            paymentSettingValue : formModel, 
            paymentSetting: paymentSetting, 
            validationMap : validationMap});
    }
    
    renderInput(key : string) { 
        const comments : String = this.state.paymentSetting.value[this.unformatKey(key)].comments;
        return <>
            <FormGroup className="form-group">
                <label className="col-sm-2 control-label">{this.unformatKey(key)}</label>
                <div className="col-sm-10">
                    <FormInput  model={key} validators={[Validators.required()]} type="text"  className="form-control" />
                    <span className={"help"}>{comments}</span>
                </div>
            </FormGroup>
            <div className="hr-line-dashed"></div>
        </>
    }

    renderContent() {
        if(!this.state.paymentSettingValue) {
            return <span></span>
        }
        return (<div>
                <div className="wrapper wrapper-content animated fadeInRight">
                <Form className="form form-horizontal" validationMap={this.state.validationMap} modelObject={this.state.paymentSettingValue} formListener={this}>
                    <div className="row">
                        <div className="col-lg-12">
                            <FormSubmitError />
                            <FormSubmitSuccess text="Setting was updated successfully"/>
                            <div className="card-box">
                                {Object.keys(this.state.paymentSettingValue).sort().map((key, index) => {
                                    return this.renderInput(key);
                                })}
                            </div>
                        </div>
                    </div>  
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card-box">
                                <div className="form-group">
                                    <div className="col-sm-12">
                                        <Submit className="btn  btn-primary">Save</Submit>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </Form>
                </div>
                
        </div>)
    }

}

export default withRouter(PaymentSettingEdit);